/**
 * ContactPicker — live-search contact picker with inline "+ New contact"
 *
 * Better than a plain Combobox for the New Enquiry page because:
 *   - Search input is always visible (no click-to-open surprise)
 *   - Server-side search via /contacts?q=... so it finds contacts even in
 *     large tenants (Combobox filters the pre-fetched batch of 500)
 *   - Inline "+ New contact" launches a dialog with the full ContactForm,
 *     then auto-selects the created contact
 *
 * Used from NewDealPage. Reusable elsewhere any time a broker needs to
 * pick a contact and might not have created it yet.
 */
import { useEffect, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Search, X, UserPlus, Check, Loader2 } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { ContactForm } from "@/components/forms/ContactForm"
import {
  contactsApi,
  usersApi,
  type ContactInput,
} from "@/lib/api"
import { cn } from "@/lib/utils"

type MinContact = {
  id: string
  firstName: string
  lastName: string
  businessName: string | null
  email: string | null
  mobile: string | null
}

function useDebounced<T>(v: T, ms = 200): T {
  const [d, setD] = useState(v)
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms)
    return () => clearTimeout(t)
  }, [v, ms])
  return d
}

export function ContactPicker({
  value,
  onChange,
  onContactCreated,
  placeholder = "Search a contact or add new…",
  excludeId,
  compact,
  autoFocus,
}: {
  value: string
  onChange: (id: string) => void
  /**
   * Fires when the broker adds a NEW contact inline via the picker
   * dialog. Parent gets the full created contact so it can prefill
   * downstream form fields (e.g. deal.leadSource from contact.source,
   * ownerId, business name into deal name, etc.). Skipped when the
   * broker picks an existing contact — use the value/onChange pair for
   * that path plus a separate `contactsApi.get(id)` fetch if needed.
   */
  onContactCreated?: (contact: {
    id: string
    firstName: string
    lastName: string
    businessName: string | null
    email: string | null
    mobile: string | null
    ownerId: string | null
    source: string | null
    industry: string | null
    abn: string | null
  }) => void
  placeholder?: string
  /** Hide this contact from results — used for co-applicant picker to
   *  prevent picking the primary applicant twice. */
  excludeId?: string
  compact?: boolean
  autoFocus?: boolean
}) {
  const [q, setQ] = useState("")
  const [open, setOpen] = useState(false)
  const debouncedQ = useDebounced(q, 200)
  const [newContactOpen, setNewContactOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const qc = useQueryClient()

  // Search endpoint. Empty query returns 20 most-recent contacts so brokers
  // see something immediately when they focus the input.
  const search = useQuery({
    queryKey: ["contact-picker", debouncedQ],
    queryFn: () =>
      contactsApi.list({
        q: debouncedQ || undefined,
        pageSize: 20,
      }),
  })

  // Hydrate the "selected" label when the parent passes a value we don't
  // have loaded in the current results (e.g. preselectedContactId from URL,
  // or contact was created just now via the inline dialog).
  const selectedFromResults = search.data?.items.find((c) => c.id === value)
  const hydrate = useQuery({
    queryKey: ["contact-picker-hydrate", value],
    queryFn: () => contactsApi.get(value),
    enabled: !!value && !selectedFromResults,
  })
  const selected: MinContact | null = selectedFromResults
    ? selectedFromResults
    : hydrate.data?.contact
      ? {
          id: hydrate.data.contact.id,
          firstName: hydrate.data.contact.firstName,
          lastName: hydrate.data.contact.lastName,
          businessName: hydrate.data.contact.businessName,
          email: hydrate.data.contact.email,
          mobile: hydrate.data.contact.mobile,
        }
      : null

  // Close dropdown on click-outside
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", onClick)
    return () => document.removeEventListener("mousedown", onClick)
  }, [open])

  const brokers = useQuery({
    queryKey: ["users-active"],
    queryFn: () => usersApi.list(),
    enabled: newContactOpen,
  })

  const createContact = useMutation({
    // We use mutateAsync + a variables closure to capture the ContactInput
    // that was submitted, then combine with the created id so the callback
    // gets a merged "created contact" object without needing a second
    // GET /contacts/:id round-trip.
    mutationFn: async (data: ContactInput) => {
      const r = await contactsApi.create(data)
      return { id: r.id, submitted: data }
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["contacts-picker"] })
      qc.invalidateQueries({ queryKey: ["contact-picker"] })
      qc.invalidateQueries({ queryKey: ["contact-picker-hydrate"] })
      qc.invalidateQueries({ queryKey: ["contacts"] })
      // Fire callback FIRST so parent form can absorb prefill data before
      // it observes the id change (which triggers its own hydrate query).
      if (onContactCreated) {
        const s = r.submitted
        onContactCreated({
          id: r.id,
          firstName: s.firstName ?? "",
          lastName: s.lastName ?? "",
          businessName: s.businessName ?? null,
          email: s.email ?? null,
          mobile: s.mobile ?? null,
          ownerId: s.ownerId ?? null,
          source: s.source ?? null,
          industry: s.industry ?? null,
          abn: s.abn ?? null,
        })
      }
      onChange(r.id)
      setNewContactOpen(false)
    },
  })

  const results = (search.data?.items ?? []).filter(
    (c) => !excludeId || c.id !== excludeId
  )

  const contactLabel = (c: MinContact): string =>
    c.businessName ||
    `${c.firstName} ${c.lastName}`.trim() ||
    c.email ||
    "(unnamed contact)"

  const contactSublabel = (c: MinContact): string | null => {
    const person = `${c.firstName} ${c.lastName}`.trim()
    if (c.businessName && person && person !== c.businessName) return person
    if (c.email) return c.email
    if (c.mobile) return c.mobile
    return null
  }

  return (
    <>
      <div ref={ref} className="relative">
        {selected && !open ? (
          // Compact selected-state chip
          <div
            className={cn(
              "border-input bg-background flex items-center justify-between gap-2 rounded-md border px-3",
              compact ? "h-9" : "h-10"
            )}
          >
            <button
              type="button"
              className="flex-1 min-w-0 text-left"
              onClick={() => {
                setOpen(true)
                setQ("")
              }}
            >
              <div className="truncate text-sm font-medium text-foreground">
                {contactLabel(selected)}
              </div>
              {contactSublabel(selected) && (
                <div className="truncate text-xs text-muted-foreground">
                  {contactSublabel(selected)}
                </div>
              )}
            </button>
            <button
              type="button"
              onClick={() => onChange("")}
              className="text-muted-foreground hover:text-foreground p-0.5"
              aria-label="Clear"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="relative">
            <Search className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
            <Input
              type="text"
              placeholder={placeholder}
              value={q}
              onFocus={() => setOpen(true)}
              onChange={(e) => {
                setQ(e.target.value)
                setOpen(true)
              }}
              autoFocus={autoFocus}
              className="pl-9 pr-3"
            />
          </div>
        )}

        {open && (
          <div className="border-border absolute left-0 right-0 top-full z-40 mt-1 rounded-md border bg-white shadow-lg">
            <ul className="max-h-72 overflow-y-auto py-1">
              {search.isFetching && (
                <li className="px-3 py-2 text-xs text-muted-foreground flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Searching…
                </li>
              )}
              {!search.isFetching && results.length === 0 && (
                <li className="px-3 py-4 text-center text-sm text-muted-foreground">
                  {q ? `No contacts match "${q}"` : "No contacts yet"}
                </li>
              )}
              {results.map((c) => {
                const active = c.id === value
                return (
                  <li
                    key={c.id}
                    onClick={() => {
                      onChange(c.id)
                      setOpen(false)
                      setQ("")
                    }}
                    className={cn(
                      "hover:bg-rc-grey-50 flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm",
                      active && "bg-rc-blue-50"
                    )}
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium text-foreground">
                        {contactLabel(c)}
                      </div>
                      {contactSublabel(c) && (
                        <div className="truncate text-xs text-muted-foreground">
                          {contactSublabel(c)}
                        </div>
                      )}
                    </div>
                    {active && <Check className="text-rc-blue-600 h-4 w-4 shrink-0" />}
                  </li>
                )
              })}
            </ul>
            <div className="border-border border-t p-1">
              <button
                type="button"
                onClick={() => {
                  setOpen(false)
                  setNewContactOpen(true)
                }}
                className="hover:bg-rc-blue-50 flex w-full items-center gap-2 rounded px-3 py-2 text-sm font-medium text-rc-blue-700"
              >
                <UserPlus className="h-4 w-4" />
                Add new contact{q ? ` "${q}"` : ""}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Inline create-contact dialog. Uses the same ContactForm that the
          full New Contact page uses so the shape stays consistent. */}
      <Dialog open={newContactOpen} onOpenChange={setNewContactOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Add new contact</DialogTitle>
          </DialogHeader>
          {createContact.error && (
            <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-3 text-sm">
              {createContact.error instanceof Error
                ? createContact.error.message
                : "Failed to create contact"}
            </div>
          )}
          <ContactForm
            brokers={brokers.data?.users ?? []}
            initial={
              // Prefill firstName from what the broker typed if it looks like
              // a person name (contains a space) — saves them retyping.
              q.trim() && q.trim().includes(" ")
                ? {
                    firstName: q.trim().split(/\s+/)[0] ?? "",
                    lastName: q.trim().split(/\s+/).slice(1).join(" "),
                  }
                : q.trim()
                  ? { firstName: q.trim(), lastName: "" }
                  : undefined
            }
            onSubmit={async (data) => {
              await createContact.mutateAsync(data)
            }}
            onCancel={() => setNewContactOpen(false)}
            isSubmitting={createContact.isPending}
            submitLabel="Create & select"
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
